import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { catchError, map, Observable, take } from 'rxjs';

import { FirestoreError, Timestamp } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import axios, { AxiosRequestConfig } from 'axios';
import { NGXLogger } from 'ngx-logger';
import { sort } from 'radash';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import { Lesson } from '../models/lesson.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolLessonService {
  http = inject(HttpClient);
  private functions = inject(Functions);
  private logger: NGXLogger = inject(NGXLogger);
  afs: AngularFirestore = inject(AngularFirestore);
  utilsService = inject(UtilsService);

  constructor() {}

  getLessons({
    groupId,
    gradeId,
    avgAge,
    tags,
    limit,
    lastRecord,
  }: {
    groupId: string;
    gradeId?: string;
    avgAge?: number;
    tags?: string[];
    limit?: number;
    lastRecord?: Timestamp;
  }): Observable<Lesson[]> {
    this.logger.debug(
      `SchoolLessonService :: getLessons: Fetching lessons for groupId: ${groupId}, gradeId: ${gradeId}, avgAge: ${avgAge}, tags: ${tags}, limit: ${limit}, lastRecord: ${lastRecord}`,
    );
    let query: AngularFirestoreCollection<Lesson> = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('lessons', (ref) => {
        var reference = ref.where('groupId', '==', groupId);

        if (gradeId) {
          reference = reference.where('gradeId', '==', gradeId);
        }

        if (avgAge) {
          reference = reference.where('avgAge', '==', avgAge);
        }

        if (tags) {
          reference = reference.where('tags', 'array-contains-any', tags);
        }

        reference = reference.limit(limit ?? 10);

        reference = reference.orderBy('modified', 'desc');

        if (lastRecord) reference = reference.startAfter(lastRecord);

        return reference;
      });

    return query.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data: Lesson = a.data() as Lesson;
          data.id = a.id;
          return data;
        });
      }),
      catchError((error: FirestoreError) => {
        this.logger.error(`SchoolLessonService :: getLessons: Error: ${error}`);
        return [];
      }),
    );
  }
  catch(error: any) {
    this.logger.error(
      `SchoolLessonService :: getLessons: Error: ${error?.toString()}`,
    );
    const appException: AppExceptionModel = {
      code: 'ERR_GET_LESSONS_BY_GROUP',
      message: 'Unable to fetch Lessons at this time. Please try again later',
      title: 'Error Fetching Lessons',
    };
    throw appException;
  }

  getLessonById(groupId: string, id: string): Observable<Lesson> {
    const doc = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('lessons')
      .doc(id)
      .get();
    return doc.pipe(
      take(1),
      map((doc) => {
        return { ...doc.data(), id: id } as Lesson;
      }),
    );
  }

  async create(groupId: string, lesson: Lesson): Promise<string> {
    this.logger.debug(
      'SchoolLessonService :: create:',
      JSON.stringify(lesson, null, 2),
    );
    const doc = await this.afs
      .collection('groups')
      .doc(groupId)
      .collection('lessons')
      .add(lesson);
    return doc.id;
  }

  async update(groupId: string, id: string, lesson: Lesson): Promise<void> {
    this.logger.debug(
      `SchoolLessonService :: update: ${id}: `,
      JSON.stringify(lesson, null, 2),
    );
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('lessons')
      .doc(id)
      .update(lesson);
  }

  async getNotes(url: string) {
    this.logger.debug('SchoolLessonService :: getNotes: ', url);
    const config: AxiosRequestConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return axios(url)
      .then((response) => {
        // console.debug(
        //   `LessonService:: getNotes: Response: ${JSON.stringify(response.data)}`
        // );
        return response.data;
      })
      .catch((error) => {
        // console.error(`LessonService:: getNotes: Error: ${error}`);
        throw error;
      });
  }

  async getUploadUrl(params: Object): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(environment['AWS_SCHOOL_LESSON_SIGNEDURL']!, {
          params: params,
          headers: headers,
        });
      })
      .then((response) => {
        return {
          method: 'PUT',
          url: response.data.url,
          path: response.data.path,
          headers: {
            'Content-Type': `application/json`,
          },
        };
      });
  }

  async uploadNotes(url: string, data: any): Promise<any> {
    try {
      return axios
        .put(url, data, {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
        })
        .then((response) => {
          // console.log('LessonService:: uploadLesson:', response);
          return response;
        });
    } catch (error) {
      this.logger.error(
        'SchoolLessonService:: uploadLesson:',
        JSON.stringify(error),
      );
    }
  }

  async delete(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Lesson>('lessons')
      .doc(id)
      .delete();
  }

  async searchLessons(
    searchTerm: string,
    groupId: string,
    filterFields?: Object,
    rangeFields?: Object[],
    limit?: number,
    from?: number,
    pageSize?: number,
  ): Promise<Lesson[]> {
    this.logger.debug(
      `SchoolLessonService :: searchLessons: Search '${searchTerm}' in group '${groupId}'`,
    );
    try {
      const callable = httpsCallable(this.functions, 'searchLessonsByFilters'); //searchLessonsByFilters
      const payload: any = {
        searchTerm: searchTerm,
        groupId: groupId,
        filterFields: filterFields,
        rangeFields: rangeFields,
      };
      if (limit !== undefined) {
        payload.limit = limit;
      }
      if (from !== undefined) {
        payload.from = from;
      }
      if (pageSize !== undefined) {
        payload.pageSize = pageSize;
      }
      const result = await callable(payload);
      const data: any[] = (result.data as any[]) ?? [];
      return data.map((item) => item.lesson);
    } catch (error) {
      this.logger.error(
        `SchoolLessonService :: searchLessons: Error: ${error}`,
      );
      return Promise.reject('Unable to search lessons');
    }
  }
}
